import { useState, useEffect } from "react";
import axios from "axios";
import { X, Plus, Trash2, Save, Building2, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
};

const InvoiceEditor = ({ bookingId, invoice, token, onSave, onClose }) => {
  const [form, setForm] = useState({
    line_items: [],
    hsn_code: "9985",
    gst_type: "igst",
    place_of_supply: "",
    tax_rate: 5,
    service_charge: 0,
    notes: "",
    due_date: "",
    invoice_date: "",
    bill_to: {},
    company_details: {},
    trip_details: {}
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) {
      setForm({
        line_items: invoice.line_items?.map(it => ({ ...it })) || [],
        hsn_code: invoice.hsn_code || "9985",
        gst_type: invoice.gst_type || "igst",
        place_of_supply: invoice.place_of_supply || "",
        tax_rate: invoice.tax_rate || 5,
        service_charge: invoice.service_charge || 0,
        notes: invoice.notes || "",
        due_date: invoice.due_date || "",
        invoice_date: invoice.invoice_date || "",
        bill_to: { ...(invoice.bill_to || {}) },
        company_details: { ...(invoice.company_details || {}) },
        trip_details: { ...(invoice.trip_details || {}) }
      });
    }
  }, [invoice]);

  // GST Calculation Logic:
  // 18% GST → GST = 18% of service_charge only
  // 5% GST  → GST = 5% of (sub_total + service_charge)
  const subTotal = form.line_items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const sc = Number(form.service_charge) || 0;
  const rate = Number(form.tax_rate) || 0;
  const taxBase = rate === 18 ? sc : (subTotal + sc);
  const taxAmount = Math.round(taxBase * rate / 100 * 100) / 100;
  const total = Math.round((subTotal + sc + taxAmount) * 100) / 100;

  const updateItem = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: field === 'description' ? value : Number(value) || 0 };
    if (field === 'rate' || field === 'qty') {
      items[idx].amount = (items[idx].qty || 1) * (items[idx].rate || 0);
    }
    setForm(f => ({ ...f, line_items: items }));
  };

  const addItem = () => {
    setForm(f => ({
      ...f,
      line_items: [...f.line_items, { description: "", hsn_code: f.hsn_code, qty: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${API}/bookings/${bookingId}/invoice`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice updated!");
      if (onSave) onSave(res.data.invoice);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update invoice");
    } finally { setSaving(false); }
  };

  const co = form.company_details;
  const bt = form.bill_to;
  const trip = form.trip_details;
  const gstOn = bt.gstin && bt.gstin.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6" data-testid="invoice-editor-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4" onClick={e => e.stopPropagation()}>
        {/* Header Bar */}
        <div className="flex items-center justify-between p-4 border-b bg-violet-50 rounded-t-xl">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-bold text-gray-900">Edit Tax Invoice</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-violet-100 rounded-lg transition-colors" data-testid="close-invoice-editor"><X className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* ===== TAX INVOICE HEADER ===== */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
            <p className="text-center text-xs font-bold text-gray-500 tracking-[0.3em] uppercase mb-4">Tax Invoice</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Details - READ ONLY */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-violet-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Company Details</span>
                </div>
                <div className="bg-white rounded-lg border border-gray-100 p-3">
                  <p className="text-sm font-bold text-gray-900" data-testid="company-name-display">{co.name || '-'}</p>
                  <p className="text-xs text-gray-600 mt-1">{co.address || '-'}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-gray-500">
                    {co.phone && <span>Ph: {co.phone}</span>}
                    {co.email && <span>Email: {co.email}</span>}
                    {co.website && <span>Web: {co.website}</span>}
                  </div>
                  {co.gstin && (
                    <p className="text-xs font-mono text-gray-700 mt-1.5 bg-gray-50 rounded px-2 py-0.5 inline-block" data-testid="company-gstin-display">
                      GST: {co.gstin}
                    </p>
                  )}
                </div>
              </div>

              {/* Invoice Meta (Right) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-violet-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice Details</span>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 font-medium">Invoice #</span>
                    <span className="text-sm font-mono font-bold text-violet-700" data-testid="invoice-editor-number">{invoice?.invoice_number || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Invoice Date</span>
                    <Input value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} className="h-7 text-sm text-right max-w-[160px]" data-testid="invoice-date-input" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 font-medium">Booking ID</span>
                    <span className="text-sm font-mono font-bold text-gray-800" data-testid="invoice-editor-booking-ref">#{invoice?.booking_ref || '-'}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-0.5 block">Place of Supply</label>
                  <Input value={form.place_of_supply} onChange={e => setForm(f => ({ ...f, place_of_supply: e.target.value }))} className="h-8 text-sm" data-testid="place-of-supply-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-0.5 block">GST Type</label>
                    <select
                      value={form.gst_type}
                      onChange={e => setForm(f => ({ ...f, gst_type: e.target.value }))}
                      className="w-full h-8 rounded-md border border-gray-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      data-testid="gst-type-select"
                    >
                      <option value="igst">IGST (Inter-State)</option>
                      <option value="intra">CGST + SGST (Intra-State)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-0.5 block">Tax Rate (%)</label>
                    <Input type="number" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: Number(e.target.value) || 0 }))} className="h-8 text-sm" data-testid="tax-rate-input" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== BILL TO - READ ONLY ===== */}
          <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-bold text-blue-600 tracking-wider uppercase">Bill To</span>
            </div>
            <div className="bg-white rounded-lg border border-blue-100 p-3">
              <p className="text-sm font-bold text-gray-900" data-testid="bill-to-company-display">{bt.company || bt.name || '-'}</p>
              {bt.address && <p className="text-xs text-gray-600 mt-1">{bt.address}</p>}
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-gray-500">
                {bt.phone && <span>Ph: {bt.phone}</span>}
                {bt.email && <span>Email: {bt.email}</span>}
              </div>
              {gstOn && (
                <p className="text-xs font-mono text-gray-700 mt-1.5 bg-blue-50 rounded px-2 py-0.5 inline-block" data-testid="bill-to-gstin-display">
                  GST: {bt.gstin}
                </p>
              )}
            </div>
          </div>

          {/* ===== TRIP DETAILS ===== */}
          <div className="bg-emerald-50/50 rounded-xl border border-emerald-100 p-5">
            <p className="text-xs font-bold text-emerald-600 tracking-wider uppercase mb-3">Trip Details</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-400 mb-0.5 block">Destination</label>
                <p className="text-sm font-medium text-gray-800 h-8 flex items-center" data-testid="trip-destination-display">{trip.destination || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-0.5 block">Travel Dates</label>
                <p className="text-sm font-medium text-gray-800 h-8 flex items-center" data-testid="trip-travel-dates">
                  {formatDateDisplay(trip.travel_date)} - {formatDateDisplay(trip.return_date)}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-0.5 block">Pax</label>
                <p className="text-sm font-medium text-gray-800 h-8 flex items-center" data-testid="trip-pax-display">{trip.pax || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-0.5 block">Guest Name</label>
                <p className="text-sm font-medium text-gray-800 h-8 flex items-center" data-testid="trip-guest-name-display">{trip.guest_name || '-'}</p>
              </div>
            </div>
          </div>

          {/* ===== LINE ITEMS ===== */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-600 tracking-wider uppercase">Line Items</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-400">HSN/SAC</label>
                  <Input value={form.hsn_code} onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value }))} className="h-7 text-xs w-20" data-testid="hsn-code-input" />
                </div>
                <Button variant="outline" size="sm" onClick={addItem} className="h-7 text-xs" data-testid="add-line-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs">
                    <th className="text-left p-2.5 w-2/5">Description</th>
                    <th className="text-left p-2.5 w-1/6">HSN</th>
                    <th className="text-right p-2.5 w-1/12">Qty</th>
                    <th className="text-right p-2.5 w-1/6">Rate</th>
                    <th className="text-right p-2.5 w-1/6">Amount</th>
                    <th className="p-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.line_items.map((item, idx) => (
                    <tr key={idx} className="border-t" data-testid={`line-item-row-${idx}`}>
                      <td className="p-2">
                        <textarea
                          value={item.description}
                          onChange={e => updateItem(idx, 'description', e.target.value)}
                          className="w-full border rounded px-2 py-1.5 text-sm min-h-[44px] resize-none focus:outline-none focus:ring-1 focus:ring-violet-400"
                          rows={2}
                        />
                      </td>
                      <td className="p-2">
                        <Input className="h-8 text-xs" value={item.hsn_code || form.hsn_code} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} />
                      </td>
                      <td className="p-2">
                        <Input type="number" className="h-8 text-right text-xs" value={item.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} />
                      </td>
                      <td className="p-2">
                        <Input type="number" className="h-8 text-right text-xs" value={item.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} />
                      </td>
                      <td className="p-2 text-right font-medium text-gray-800">
                        {fmt(item.amount)}
                      </td>
                      <td className="p-2">
                        <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== TOTALS ===== */}
          <div className="flex justify-end">
            <div className="w-96 space-y-2 text-sm bg-gray-50 rounded-lg p-4 border">
              <div className="flex justify-between">
                <span className="text-gray-500">Sub Total</span>
                <span className="font-medium">{fmt(subTotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Service Charge</span>
                <Input
                  type="number"
                  value={form.service_charge}
                  onChange={e => setForm(f => ({ ...f, service_charge: Number(e.target.value) || 0 }))}
                  className="h-7 text-sm text-right w-32"
                  data-testid="service-charge-input"
                />
              </div>
              {rate === 18 && (
                <p className="text-[10px] text-amber-600 text-right italic">GST 18% applied on Service Charge only</p>
              )}
              {rate !== 18 && rate > 0 && (
                <p className="text-[10px] text-amber-600 text-right italic">GST {rate}% applied on (Sub Total + Service Charge)</p>
              )}
              {form.gst_type === 'igst' ? (
                <div className="flex justify-between"><span className="text-gray-500">IGST @ {rate}%</span><span>{fmt(taxAmount)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-gray-500">CGST @ {(rate / 2).toFixed(1)}%</span><span>{fmt(taxAmount / 2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">SGST @ {(rate / 2).toFixed(1)}%</span><span>{fmt(taxAmount / 2)}</span></div>
                </>
              )}
              <div className="flex justify-between pt-2 border-t font-bold text-base">
                <span>Total (INR)</span>
                <span className="text-emerald-700" data-testid="invoice-editor-total">{fmt(total)}</span>
              </div>
            </div>
          </div>

          {/* ===== NOTES ===== */}
          <div>
            <label className="text-xs font-bold text-gray-500 tracking-wider uppercase mb-1 block">Notes / Terms</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px] resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="Add any notes or payment terms..."
              data-testid="invoice-notes-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-invoice-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceEditor;
